/**
 * Local index of product names (and light metadata) from POS / dispensing stock-sheet uploads.
 * Used for offline-friendly autocomplete when typing batch-scan lines manually — no network required.
 */

export const POS_REFERENCE_EVENT = "ed-pos-reference-updated";

export type PosReferenceEntry = {
  /** Display name as on the sheet */
  name: string;
  dosage: string;
  unit: string;
  /** Stock qty from export when present (on-hand hint only). */
  qty: number | null;
};

export type PosReferenceRowV1 = {
  userId: string;
  updatedAt: number;
  entries: PosReferenceEntry[];
};

const DB_NAME = "ed_expirydesk_posref";
const DB_VERSION = 1;
const STORE = "pos_ref";
const MAX_ENTRIES = 12_000;

const LS_PREFIX = "ed_pos_ref_v1_";

function normalizeKey(name: string): string {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** Drop nulls / malformed rows so a bad IndexedDB or localStorage blob cannot crash autocomplete. */
function sanitizePosReferenceEntries(raw: unknown): PosReferenceEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: PosReferenceEntry[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const name = typeof o.name === "string" ? o.name.trim() : "";
    if (!name) continue;
    const dosage = typeof o.dosage === "string" ? o.dosage : "";
    const unitRaw = typeof o.unit === "string" ? o.unit.trim() : "";
    const unit = unitRaw || "boxes";
    const qty = typeof o.qty === "number" && Number.isFinite(o.qty) ? o.qty : null;
    out.push({ name, dosage, unit, qty });
  }
  return out;
}

function legacyKey(userId: string) {
  return `${LS_PREFIX}${userId}`;
}

function notify() {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(POS_REFERENCE_EVENT));
  } catch {
    /* ignore */
  }
}

let idbSingleton: IDBDatabase | null = null;
let idbOpenPromise: Promise<IDBDatabase | null> | null = null;

function attachDbLifecycle(db: IDBDatabase) {
  db.onversionchange = () => {
    try {
      db.close();
    } catch {
      /* ignore */
    }
    idbSingleton = null;
    idbOpenPromise = null;
  };
}

function getIndexedDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  if (idbSingleton) return Promise.resolve(idbSingleton);
  if (idbOpenPromise) return idbOpenPromise;

  idbOpenPromise = new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onerror = () => {
        idbOpenPromise = null;
        resolve(null);
      };
      req.onsuccess = () => {
        const db = req.result;
        attachDbLifecycle(db);
        idbSingleton = db;
        idbOpenPromise = null;
        resolve(db);
      };
      req.onupgradeneeded = () => {
        const database = req.result;
        if (!database.objectStoreNames.contains(STORE)) {
          database.createObjectStore(STORE, { keyPath: "userId" });
        }
      };
    } catch {
      idbOpenPromise = null;
      resolve(null);
    }
  });
  return idbOpenPromise;
}

async function idbGet(userId: string): Promise<PosReferenceRowV1 | null> {
  const db = await getIndexedDb();
  if (!db) return null;
  try {
    const row = await new Promise<PosReferenceRowV1 | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      tx.onerror = () => reject(tx.error);
      const r = tx.objectStore(STORE).get(userId);
      r.onsuccess = () => resolve(r.result as PosReferenceRowV1 | undefined);
    });
    if (!row || !Array.isArray(row.entries)) return null;
    return row;
  } catch {
    return null;
  }
}

async function idbPut(row: PosReferenceRowV1): Promise<boolean> {
  const db = await getIndexedDb();
  if (!db) return false;
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(STORE).put(row);
    });
    return true;
  } catch {
    return false;
  }
}

function legacyLoad(userId: string): PosReferenceRowV1 | null {
  if (typeof window === "undefined" || !userId) return null;
  try {
    const raw = window.localStorage.getItem(legacyKey(userId));
    if (!raw) return null;
    const p = JSON.parse(raw) as unknown;
    if (!p || typeof p !== "object") return null;
    const o = p as PosReferenceRowV1;
    if (o.userId !== userId || !Array.isArray(o.entries)) return null;
    return o;
  } catch {
    return null;
  }
}

function legacySave(row: PosReferenceRowV1): boolean {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.setItem(legacyKey(row.userId), JSON.stringify(row));
    return true;
  } catch {
    return false;
  }
}

function legacyClear(userId: string) {
  try {
    window.localStorage.removeItem(legacyKey(userId));
  } catch {
    /* ignore */
  }
}

/** Merge sheet parse lines into the device-local reference list (deduped by normalized name). */
export async function mergePosReferenceFromParsed(
  userId: string | null | undefined,
  meds: unknown[],
): Promise<void> {
  if (typeof window === "undefined" || !userId || !Array.isArray(meds) || meds.length === 0) return;

  let existing = (await idbGet(userId)) ?? legacyLoad(userId);
  const byKey = new Map<string, PosReferenceEntry>();

  if (existing?.entries?.length) {
    for (const e of existing.entries) {
      if (e && typeof e.name === "string" && e.name.trim()) {
        byKey.set(normalizeKey(e.name), {
          name: e.name.trim(),
          dosage: typeof e.dosage === "string" ? e.dosage : "",
          unit: typeof e.unit === "string" ? e.unit : "boxes",
          qty: typeof e.qty === "number" && Number.isFinite(e.qty) ? e.qty : null,
        });
      }
    }
  }

  for (const raw of meds) {
    if (!raw || typeof raw !== "object") continue;
    const m = raw as Record<string, unknown>;
    const name = String(m.name ?? "").trim();
    if (!name) continue;
    const dosage = String(m.dosage ?? "").trim();
    const unit = String(m.unit ?? "boxes").trim() || "boxes";
    const q = m.quantity;
    const qty = typeof q === "number" && Number.isFinite(q) ? q : null;
    const key = normalizeKey(name);
    const prev = byKey.get(key);
    if (!prev || name.length >= prev.name.length) {
      byKey.set(key, { name, dosage, unit, qty: qty ?? prev?.qty ?? null });
    }
  }

  let entries = Array.from(byKey.values());
  entries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  if (entries.length > MAX_ENTRIES) {
    entries = entries.slice(entries.length - MAX_ENTRIES);
  }

  const row: PosReferenceRowV1 = {
    userId,
    updatedAt: Date.now(),
    entries,
  };

  const ok = await idbPut(row);
  if (ok) {
    legacyClear(userId);
  } else {
    legacySave(row);
  }
  notify();
}

export async function loadPosReference(userId: string | null | undefined): Promise<PosReferenceEntry[]> {
  if (typeof window === "undefined" || !userId) return [];
  const fromIdb = await idbGet(userId);
  if (fromIdb?.entries?.length) {
    legacyClear(userId);
    return sanitizePosReferenceEntries(fromIdb.entries);
  }
  const leg = legacyLoad(userId);
  return leg?.entries?.length ? sanitizePosReferenceEntries(leg.entries) : [];
}

/** Filter reference names for autocomplete (substring match, stable order). */
export function filterPosReferenceSuggestions(
  entries: PosReferenceEntry[],
  query: string,
  limit = 12,
): PosReferenceEntry[] {
  const q = normalizeKey(typeof query === "string" ? query : "");
  if (!q) return [];
  const list = Array.isArray(entries) ? entries : [];
  const out: { e: PosReferenceEntry; rank: number }[] = [];
  for (const e of list) {
    if (!e || typeof e !== "object" || typeof e.name !== "string") continue;
    const nk = normalizeKey(e.name);
    if (!nk.includes(q)) continue;
    const rank = nk.startsWith(q) ? 0 : 1;
    out.push({ e, rank });
  }
  out.sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    const an = String(a.e.name ?? "");
    const bn = String(b.e.name ?? "");
    return an.localeCompare(bn, undefined, { sensitivity: "base" });
  });
  return out.slice(0, limit).map((x) => x.e);
}
